import { useState, useEffect, useCallback, useRef } from 'react'
import * as Sentry from '@sentry/react'
import type { StakeholderTemplate } from '@bandos/shared'
import { StakeholderService } from '../services/stakeholderService'
import { useFeatureAccessContext } from '../../../context/featureAccess'

interface UseStakeholderTemplatesOptions {
  /** Whether to auto-load templates on mount */
  autoLoad?: boolean
  /** Polling interval in milliseconds (default: 3000) */
  pollingInterval?: number
  /** Timeout for portrait generation in milliseconds (default: 60000) */
  portraitTimeout?: number
}

interface UseStakeholderTemplatesReturn {
  /** All templates (public + private, deduplicated) */
  templates: StakeholderTemplate[]
  /** Loading state */
  loading: boolean
  /** Set of template IDs currently generating portraits */
  generatingPortraitIds: Set<string>
  /** Reload templates from server */
  loadTemplates: () => Promise<void>
  /** Mark a template as generating a portrait (starts polling) */
  markGeneratingPortrait: (templateId: string) => void
  /** Check if a template is generating a portrait */
  isGeneratingPortrait: (templateId: string) => boolean
}

/**
 * Check if two template arrays have meaningful differences.
 * Only compares fields that would affect the UI.
 */
function hasTemplateChanges(
  oldTemplates: StakeholderTemplate[],
  newTemplates: StakeholderTemplate[]
): boolean {
  if (oldTemplates.length !== newTemplates.length) return true

  for (const newT of newTemplates) {
    const oldT = oldTemplates.find((t) => t.id === newT.id)
    if (!oldT) return true
    // Check fields that matter for display
    if (
      oldT.portrait_url !== newT.portrait_url ||
      oldT.name !== newT.name ||
      oldT.role !== newT.role
    ) {
      return true
    }
  }
  return false
}

/**
 * Hook for managing stakeholder templates with portrait polling.
 * Handles loading, deduplication, and portrait generation tracking.
 */
export function useStakeholderTemplates(
  options: UseStakeholderTemplatesOptions = {}
): UseStakeholderTemplatesReturn {
  const { autoLoad = true, pollingInterval = 3000, portraitTimeout = 60000 } = options
  const { isFeatureEnabled } = useFeatureAccessContext()
  const stakeholdersEnabled = isFeatureEnabled('simulated_stakeholders')

  const [templates, setTemplates] = useState<StakeholderTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [generatingPortraitIds, setGeneratingPortraitIds] = useState<Set<string>>(new Set())
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isPollingRef = useRef(false)

  const loadTemplates = useCallback(async () => {
    try {
      // Don't set loading during polling - only on initial/manual load
      if (!isPollingRef.current) {
        setLoading(true)
      }

      const response = await StakeholderService.getTemplates()
      if (response.error || !response.data) {
        console.warn('[useStakeholderTemplates] Failed to load templates:', response.error)
        return
      }

      // Safely handle potentially undefined arrays
      const publicTemplates = response.data.public
      const privateTemplates = response.data.private
      const combined = [...publicTemplates, ...privateTemplates]
      const seen = new Set<string>()
      const allTemplates = combined.filter((t) => {
        if (seen.has(t.id)) return false
        seen.add(t.id)
        return true
      })

      // Only update templates if there are actual changes
      setTemplates((prev) => {
        if (hasTemplateChanges(prev, allTemplates)) {
          return allTemplates
        }
        return prev
      })

      // Update generating portrait IDs - remove any that now have portraits
      setGeneratingPortraitIds((prev) => {
        if (prev.size === 0) return prev
        const stillGenerating = new Set<string>()
        for (const id of prev) {
          const template = allTemplates.find((t) => t.id === id)
          if (template && !template.portrait_url) {
            stillGenerating.add(id)
          }
        }
        // Only update if something changed
        if (
          stillGenerating.size === prev.size &&
          [...prev].every((id) => stillGenerating.has(id))
        ) {
          return prev
        }
        return stillGenerating
      })
    } catch (error) {
      console.error('[useStakeholderTemplates] Error loading templates:', error)
      Sentry.captureException(error, {
        tags: { feature: 'stakeholder', action: 'load_templates' },
      })
    } finally {
      if (!isPollingRef.current) {
        setLoading(false)
      }
    }
  }, [])

  // Auto-load on mount if enabled and feature is on
  useEffect(() => {
    if (autoLoad && stakeholdersEnabled) {
      void loadTemplates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakeholdersEnabled])

  // Clear state when feature is disabled
  useEffect(() => {
    if (!stakeholdersEnabled) {
      setTemplates([])
      setGeneratingPortraitIds(new Set())
    }
  }, [stakeholdersEnabled])

  // Portrait polling effect - poll when there are generating portraits
  useEffect(() => {
    if (!stakeholdersEnabled || generatingPortraitIds.size === 0) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      isPollingRef.current = false
      return undefined
    }

    isPollingRef.current = true
    pollingIntervalRef.current = setInterval(() => {
      void loadTemplates()
    }, pollingInterval)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      isPollingRef.current = false
    }
  }, [generatingPortraitIds, pollingInterval, loadTemplates, stakeholdersEnabled])

  // Auto-remove generating IDs after timeout
  useEffect(() => {
    if (generatingPortraitIds.size === 0) return undefined

    const timeout = setTimeout(() => {
      setGeneratingPortraitIds(new Set())
    }, portraitTimeout)

    return () => clearTimeout(timeout)
  }, [generatingPortraitIds, portraitTimeout])

  const markGeneratingPortrait = useCallback((templateId: string) => {
    setGeneratingPortraitIds((prev) => {
      if (prev.has(templateId)) return prev
      return new Set([...prev, templateId])
    })
  }, [])

  const isGeneratingPortrait = useCallback(
    (templateId: string) => {
      const template = templates.find((t) => t.id === templateId)
      return generatingPortraitIds.has(templateId) && !template?.portrait_url
    },
    [templates, generatingPortraitIds]
  )

  return {
    templates,
    loading,
    generatingPortraitIds,
    loadTemplates,
    markGeneratingPortrait,
    isGeneratingPortrait,
  }
}
